import { TFunction } from "@einride/saga-core"
import { Group, Theme } from "@einride/ui"
import { convertUnit } from "components/format/Unit/Unit"
import { UnitSystem } from "components/UserPreferences/UserPreferencesContext"
import {
  Comparison,
  MetricConfiguration,
  MetricFragment,
  TimeComparisonInterval,
} from "gen/graphql"
import { havingComparison } from "pages/ReportPage/hooks/useReportState"
import { getMetricConfiguration } from "pages/ReportPage/useAvailableMetrics"
import { uppercaseFirstLetter } from "./strings"

export const translateMetricId = (label: string, t: TFunction<"explore">): string => {
  switch (label.toLowerCase()) {
    case "distance_km":
      return t("metrics.distanceDriven")
    case "co2e_emissions_kg":
      return t("metrics.co2eEmitted")
    case "co2e_emissions_savings_kg":
      return t("metrics.co2eSaved")
    case "co2e_emitted_per_tonne_km":
      return t("metrics.co2eEmittedPerTonneKm")
    case "shipment_count":
      return t("metrics.shipmentCount")
    case "diesel_benchmark":
      return t("metrics.dieselBenchmark")
    case "transport_activity":
      return t("metrics.transportActivity")
    default:
      return label
  }
}

export const translateComparedTo = (comparison: Comparison, t: TFunction<"explore">): string => {
  if (comparison.interval) {
    if (comparison.interval === TimeComparisonInterval.LastYear) {
      return t("reportPage.comparedToKey.lastYear")
    }
    if (comparison.interval === TimeComparisonInterval.PreviousPeriod) {
      return t("reportPage.comparedToKey.previousPeriod")
    }
  } else if (comparison.metric === "diesel_benchmark") {
    return t("reportPage.comparedToKey.dieselBenchmark")
  }
  return ""
}

export const translateComparedToInMetric = (
  comparison: Comparison,
  t: TFunction<"explore">,
): string => {
  if (comparison.interval) {
    if (comparison.interval === TimeComparisonInterval.LastYear) {
      return t("reportPage.comparedToInMetric.lastYear")
    }
    if (comparison.interval === TimeComparisonInterval.PreviousPeriod) {
      return t("reportPage.comparedToInMetric.previousPeriod")
    }
  } else if (comparison.metric === "diesel_benchmark") {
    return t("reportPage.comparedToInMetric.dieselBenchmark")
  }
  return ""
}

export function translateGroupName(group: string, t: TFunction<"explore">): string {
  if (/^\d+_more$/.test(group)) {
    const [count] = group.split("_")
    return `${count} ${t("common.more")}`
  }
  if (/\w+_\w+/.test(group)) {
    return group
      .split("_")
      .map((part) => uppercaseFirstLetter(part))
      .join(" ")
  }
  return uppercaseFirstLetter(group)
}

export const getMetricTitle = (
  metric: MetricConfiguration,
  availableMetrics: MetricFragment[],
  unitSystem: UnitSystem,
  mode: "long" | "short",
  theme: Theme,
  t: TFunction<"explore">,
): React.JSX.Element => {
  const metricConfiguration = getMetricConfiguration(metric?.metricId ?? "", availableMetrics)
  const metricName = [
    [
      translateMetricId(metric.metricId, t),
      metricConfiguration?.unit
        ? `${t("common.in")} ${
            metric.comparedTo?.relative
              ? "%"
              : convertUnit(metricConfiguration?.unit ?? "", unitSystem)
          }`
        : undefined,
    ].join(" "),
  ]

  let comparison
  if (metric.comparedTo?.comparison && havingComparison(metric.comparedTo.comparison)) {
    comparison = `${
      mode === "long" ? t("reportPage.comparedTo").toLowerCase() : t("common.vs")
    } ${translateComparedTo(metric.comparedTo.comparison, t)}`
  } else if (metric.groupsAsShareOfTotal) {
    comparison = `${t("reportPage.shareOfTotal")} %`
  } else if (metric.comparedTo?.relative) {
    comparison = "%"
  }

  return (
    <Group gap={1}>
      {metricName}
      {comparison && (
        <>
          <span style={{ color: theme.colors.border.primary }}>|</span>
          {comparison}
        </>
      )}
    </Group>
  )
}
